import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import redditApi from '../../../services/redditApi';
import ArchiveToggle from '../components/ArchiveToggle';

/*
 * Generic "<dimension> × Redditor" deletion-rate heat-map with a toggle to
 * switch between several analyses (Karma / Post Age / Account Age / Comment Length).
 *
 * Driven entirely by the API response (bucket_order + bucket_labels), so the
 * same component renders every dimension.
 *
 * Props:
 *   title — card heading
 *   views — array of:
 *     { key, label, subtitle, endpoint, allLabel, footnote, csvPrefix, detailExport }
 *       detailExport — optional path for row-level CSV (Comment Length)
 */

const SMALL_SAMPLE = 5;

const filterSelectStyle = {
  border: '1px solid var(--rh-border)',
  borderRadius: 8,
  padding: '7px 10px',
  fontSize: 12,
  background: '#fff',
  color: 'var(--rh-text)',
  minWidth: 140,
  maxWidth: 200,
};

const filterInputStyle = {
  ...filterSelectStyle,
  minWidth: 120,
  maxWidth: 150,
};

function heatColor(rate) {
  if (rate === 0)   return { bg: '#f0fdf4', color: '#15803d' };
  if (rate <= 10)   return { bg: '#dcfce7', color: '#16a34a' };
  if (rate <= 20)   return { bg: '#d1fae5', color: '#15803d' };
  if (rate <= 33)   return { bg: '#fef9c3', color: '#a16207' };
  if (rate <= 50)   return { bg: '#ffedd5', color: '#c2410c' };
  if (rate <= 75)   return { bg: '#fee2e2', color: '#b91c1c' };
  return              { bg: '#fecaca', color: '#991b1b' };
}

function Cell({ cell, isTotal }) {
  if (!cell || cell.submitted === 0) {
    return (
      <div style={{
        background: '#f1f5f9', borderRadius: 8, padding: '10px 14px',
        display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
        minWidth: 118, width: '90%', position: 'relative',
      }}>
        <span style={{ fontWeight: 700, fontSize: 16, color: '#cbd5e1', lineHeight: 1.15 }}>—</span>
        <span style={{ fontSize: 10, color: '#cbd5e1', marginTop: 2, fontWeight: 500 }}>0&nbsp;/&nbsp;0</span>
      </div>
    );
  }
  const { bg, color } = heatColor(cell.deletion_rate);
  const small = !isTotal && cell.submitted < SMALL_SAMPLE;
  const title = typeof cell.live === 'number'
    ? `Live: ${cell.live} · Deleted: ${cell.deleted} · Total: ${cell.submitted}`
    : `Deleted: ${cell.deleted} · Total: ${cell.submitted}`;
  return (
    <div
      title={title}
      style={{
        background: bg, borderRadius: 8, padding: '10px 14px',
        display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
        minWidth: 118, width: '90%', position: 'relative',
      }}
    >
      <span style={{ fontWeight: 700, fontSize: 16, color, lineHeight: 1.15 }}>
        {cell.deletion_rate}%
        {small && <sup style={{ fontSize: 8, marginLeft: 1, opacity: 0.65 }}>*</sup>}
      </span>
      <span style={{ fontSize: 10, color, opacity: 0.7, marginTop: 2, fontWeight: 500 }}>
        {cell.deleted}&nbsp;/&nbsp;{cell.submitted}
      </span>
    </div>
  );
}

function buildQuery(filters) {
  const params = new URLSearchParams();
  if (filters.client) params.set('client', filters.client);
  if (filters.redditorId) params.set('redditor_id', filters.redditorId);
  if (filters.subreddit) params.set('subreddit', filters.subreddit);
  if (filters.dateFrom) params.set('date_from', filters.dateFrom);
  if (filters.dateTo) params.set('date_to', filters.dateTo);
  if (filters.includeArchived) params.set('include_archived', 'true');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

function withQuery(endpoint, filters) {
  const extra = buildQuery(filters);
  if (!extra) return endpoint;
  return endpoint.includes('?') ? `${endpoint}&${extra.slice(1)}` : `${endpoint}${extra}`;
}

export default function RedditorBucketMatrix({ title, views }) {
  const [activeKey, setActiveKey] = useState(views[0]?.key);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [exporting, setExporting] = useState(false);

  const [client, setClient] = useState('');
  const [redditorId, setRedditorId] = useState('');
  const [subreddit, setSubreddit] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);

  const [clientOptions, setClientOptions] = useState([]);
  const [redditorOptions, setRedditorOptions] = useState([]);
  const [subredditOptions, setSubredditOptions] = useState([]);

  const filters = useMemo(() => ({
    client,
    redditorId,
    subreddit,
    dateFrom,
    dateTo,
    includeArchived,
  }), [client, redditorId, subreddit, dateFrom, dateTo, includeArchived]);

  // Sliding pill for the dimension toggle (measured from the active button)
  const btnRefs = useRef({});
  const [pill, setPill] = useState({ left: 0, width: 0 });

  useEffect(() => {
    function positionPill() {
      const el = btnRefs.current[activeKey];
      if (el) setPill({ left: el.offsetLeft, width: el.offsetWidth });
    }
    positionPill();
    window.addEventListener('resize', positionPill);
    return () => window.removeEventListener('resize', positionPill);
  }, [activeKey, views]);

  useEffect(() => {
    Promise.all([
      redditApi.get('/admin/clients/?status=all'),
      redditApi.get('/admin/redditors/', { params: { status: 'all' } }),
    ]).then(([clientsRes, redditorsRes]) => {
      const clients = (clientsRes.data?.clients || clientsRes.data || [])
        .map((c) => c.client_name || c.name || c)
        .filter(Boolean)
        .sort((a, b) => String(a).localeCompare(String(b)));
      setClientOptions([...new Set(clients)]);

      const raw = Array.isArray(redditorsRes.data)
        ? redditorsRes.data
        : (redditorsRes.data?.redditors || []);
      const redditors = raw
        .map((r) => ({
          id: String(r.id),
          name: r.full_name || r.name || r.email || String(r.id),
          active: r.is_active !== false,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setRedditorOptions(redditors);
    }).catch(() => {});
  }, []);

  // Distinct subreddits from comments list when possible (best-effort)
  useEffect(() => {
    redditApi.get('/admin/comments/', { params: { page_size: 200 } })
      .then(({ data: d }) => {
        const rows = d?.results || d?.comments || d || [];
        if (!Array.isArray(rows)) return;
        const subs = [...new Set(rows.map((c) => c.subreddit).filter(Boolean))]
          .sort((a, b) => a.localeCompare(b));
        if (subs.length) setSubredditOptions(subs);
      })
      .catch(() => {});
  }, []);

  const active = views.find((v) => v.key === activeKey) || views[0];
  const { endpoint, subtitle, allLabel, footnote, csvPrefix, detailExport } = active;

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    setData(null);
    redditApi.get(withQuery(endpoint, filters))
      .then(({ data: d }) => setData(d))
      .catch(() => setError('Failed to load.'))
      .finally(() => setLoading(false));
  }, [endpoint, filters]);

  useEffect(() => { load(); }, [load]);

  const labels  = data?.bucket_labels || {};
  const buckets = (data?.bucket_order || []).filter((bk) => bk !== 'unknown');
  const hasUnknown = (data?.bucket_order || []).includes('unknown');
  const unverified = data?.unverified;

  async function exportCSV() {
    if (detailExport) {
      setExporting(true);
      try {
        const { data: blob } = await redditApi.get(withQuery(detailExport, filters), {
          responseType: 'blob',
        });
        const url = URL.createObjectURL(blob);
        const now = new Date();
        const dd = String(now.getDate()).padStart(2, '0');
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const yyyy = now.getFullYear();
        const a = document.createElement('a');
        a.href = url;
        a.download = `RedditHub_${csvPrefix}_Detail_${dd}_${mm}_${yyyy}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        setError('Failed to export CSV.');
      } finally {
        setExporting(false);
      }
      return;
    }

    if (!data) return;
    const cols = [...buckets, ...(hasUnknown ? ['unknown'] : [])];

    const escape = (v) => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const bucketCols = cols.flatMap((bk) => [
      `${labels[bk] || bk} - Deletion Rate (%)`,
      `${labels[bk] || bk} - Deleted/Submitted`,
    ]);
    const headers = ['Redditor', 'Total Submitted', 'Total Deleted', 'Overall Deletion Rate (%)', ...bucketCols];

    const makeRow = (name, cells) => {
      const allSub  = cols.reduce((s, bk) => s + (cells[bk]?.submitted || 0), 0);
      const allDel  = cols.reduce((s, bk) => s + (cells[bk]?.deleted   || 0), 0);
      const allRate = allSub > 0 ? Math.round(allDel / allSub * 1000) / 10 : 0;
      const bucketCells = cols.flatMap((bk) => {
        const c = cells[bk];
        if (!c || c.submitted === 0) return ['0', '0/0'];
        return [c.deletion_rate, `${c.deleted}/${c.submitted}`];
      });
      return [name, allSub, allDel, allRate, ...bucketCells].map(escape).join(',');
    };

    const rows = data.redditors.map((r) => makeRow(r.name, r.cells));
    rows.push(makeRow('All Redditors', data.totals));

    const csv  = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const now  = new Date();
    const dd   = String(now.getDate()).padStart(2, '0');
    const mm   = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `RedditHub_${csvPrefix}_${dd}_${mm}_${yyyy}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Columns rendered in the table: named buckets + Unknown + roll-up
  const colKeys = [...buckets, ...(hasUnknown ? ['unknown'] : [])];

  const visibleRedditors = includeArchived
    ? redditorOptions
    : redditorOptions.filter((r) => r.active);

  return (
    <div className="rh-card" style={{ marginTop: 28 }}>
      {/* Card header */}
      <div style={{
        padding: '18px 20px 14px',
        borderBottom: '1px solid var(--rh-border)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 12, flexWrap: 'wrap',
      }}>
        <div>
          <h3 className="rh-card-title">{title}</h3>
          <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--rh-text-muted)' }}>
            {subtitle}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--rh-text-muted)', flexShrink: 0 }}>
            <span>Safer</span>
            <div style={{ display: 'flex', borderRadius: 99, overflow: 'hidden', height: 14, width: 120 }}>
              {['#bbf7d0', '#d1fae5', '#fef9c3', '#ffedd5', '#fecaca'].map((c, i) => (
                <div key={i} style={{ flex: 1, background: c }} />
              ))}
            </div>
            <span>Riskier</span>
          </div>
          {!loading && !error && data && (
            <button
              type="button"
              onClick={exportCSV}
              disabled={exporting}
              className="rh-btn-secondary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
          )}
        </div>
      </div>

      {/* Dimension toggle — sliding pill */}
      <div style={{ padding: '12px 20px 0' }}>
        <div style={{
          position: 'relative', display: 'inline-flex',
          background: 'var(--rh-bg, #f3f4f6)',
          border: '1px solid var(--rh-border)', borderRadius: 10, padding: 3,
        }}>
          {pill.width > 0 && (
            <div style={{
              position: 'absolute', top: 3, bottom: 3,
              left: pill.left, width: pill.width,
              background: 'var(--rh-orange)', borderRadius: 8,
              boxShadow: '0 1px 3px rgba(255,69,0,0.3)',
              transition: 'left 0.28s cubic-bezier(0.34, 1.4, 0.64, 1), width 0.28s cubic-bezier(0.34, 1.4, 0.64, 1)',
              pointerEvents: 'none',
            }} />
          )}
          {views.map((v) => {
            const isActive = v.key === activeKey;
            return (
              <button
                key={v.key}
                type="button"
                ref={(el) => { btnRefs.current[v.key] = el; }}
                onClick={() => setActiveKey(v.key)}
                style={{
                  position: 'relative', zIndex: 1,
                  border: 'none', cursor: 'pointer', background: 'transparent',
                  padding: '7px 18px', borderRadius: 8, fontSize: 13,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? '#fff' : 'var(--rh-text-muted)',
                  transition: 'color 0.2s',
                  whiteSpace: 'nowrap',
                }}
              >
                {v.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Shared filters */}
      <div style={{
        padding: '12px 20px',
        display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
        borderBottom: '1px solid var(--rh-border)',
      }}>
        <select value={client} onChange={(e) => setClient(e.target.value)} style={filterSelectStyle}>
          <option value="">All clients</option>
          {clientOptions.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
        <select value={redditorId} onChange={(e) => setRedditorId(e.target.value)} style={filterSelectStyle}>
          <option value="">All redditors</option>
          {visibleRedditors.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}{r.active ? '' : ' (archived)'}
            </option>
          ))}
        </select>
        <select value={subreddit} onChange={(e) => setSubreddit(e.target.value)} style={filterSelectStyle}>
          <option value="">All subreddits</option>
          {subredditOptions.map((s) => (
            <option key={s} value={s}>r/{s}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Or type subreddit…"
          value={subredditOptions.includes(subreddit) ? '' : subreddit}
          onChange={(e) => setSubreddit(e.target.value.replace(/^r\//i, '').trim())}
          style={filterInputStyle}
          title="Type a subreddit name (without r/) if not in the list"
        />
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={filterInputStyle} title="From date" />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={filterInputStyle} title="To date" />
        <ArchiveToggle
          checked={includeArchived}
          onChange={setIncludeArchived}
          onLabel="Including archived redditors"
          offLabel="Include archived redditors"
          title="Include deactivated/archived redditors in this analysis"
        />
      </div>

      {unverified && (unverified.submitted > 0 || unverified.dws > 0) && (
        <div style={{
          margin: '12px 20px 0',
          padding: '10px 14px',
          background: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: 8,
          fontSize: 12,
          color: '#92400e',
          lineHeight: 1.5,
        }}>
          Pending / unverified (excluded from rates):{' '}
          <strong>{unverified.submitted}</strong> submitted (not yet liveness-checked)
          {unverified.dws > 0 && (
            <> · <strong>{unverified.dws}</strong> DWS</>
          )}
        </div>
      )}

      {loading && <div style={{ padding: 40, textAlign: 'center', color: 'var(--rh-text-muted)', fontSize: 13 }}>Loading…</div>}
      {error   && <div style={{ padding: 20, color: '#b91c1c', fontSize: 13 }}>{error}</div>}

      {!loading && !error && data && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--rh-border)' }}>
                <th style={{
                  padding: '10px 20px', textAlign: 'left',
                  fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '0.07em', color: 'var(--rh-text-muted)',
                  whiteSpace: 'nowrap', minWidth: 170, width: 180,
                }}>Redditor</th>
                {colKeys.map((bk) => {
                  const total = data.totals[bk]?.submitted || 0;
                  return (
                    <th key={bk} style={{
                      padding: '10px 6px', textAlign: 'center',
                      fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '0.05em', color: 'var(--rh-text-muted)', whiteSpace: 'nowrap',
                    }}>
                      <div>{labels[bk] || bk}</div>
                      {total > 0 && (
                        <div style={{ fontWeight: 400, fontSize: 10, marginTop: 2, color: '#94a3b8' }}>
                          {total} submitted
                        </div>
                      )}
                    </th>
                  );
                })}
                <th style={{
                  padding: '10px 6px', textAlign: 'center',
                  fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '0.05em', color: 'var(--rh-text-muted)', whiteSpace: 'nowrap',
                  borderLeft: '2px solid var(--rh-border)',
                }}>
                  <div>{allLabel || 'Overall'}</div>
                  {(() => {
                    const t = colKeys.reduce((s, bk) => s + (data.totals[bk]?.submitted || 0), 0);
                    return t > 0
                      ? <div style={{ fontWeight: 400, fontSize: 10, marginTop: 2, color: '#94a3b8' }}>{t} submitted</div>
                      : null;
                  })()}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.redditors.map((r) => {
                const initials = r.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
                const allSub = colKeys.reduce((s, bk) => s + (r.cells[bk]?.submitted || 0), 0);
                const allDel = colKeys.reduce((s, bk) => s + (r.cells[bk]?.deleted   || 0), 0);
                const allLive = colKeys.reduce((s, bk) => s + (r.cells[bk]?.live || 0), 0);
                const allRate = allSub > 0 ? Math.round(allDel / allSub * 1000) / 10 : 0;
                const overallCell = {
                  submitted: allSub,
                  deleted: allDel,
                  deletion_rate: allRate,
                  ...(typeof r.cells[colKeys[0]]?.live === 'number' ? { live: allLive } : {}),
                };
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--rh-border)' }}>
                    <td style={{ padding: '10px 16px', whiteSpace: 'nowrap', width: 180 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                          background: 'rgba(255,69,0,0.1)', color: 'var(--rh-orange)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700, letterSpacing: '0.03em',
                        }}>{initials}</div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--rh-text)' }}>{r.name}</div>
                          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{allSub} submitted</div>
                        </div>
                      </div>
                    </td>
                    {colKeys.map((bk) => (
                      <td key={bk} style={{ padding: '8px 5px', textAlign: 'center' }}>
                        <Cell cell={r.cells[bk]} />
                      </td>
                    ))}
                    <td style={{ padding: '8px 5px', textAlign: 'center', borderLeft: '2px solid var(--rh-border)' }}>
                      <Cell cell={overallCell} />
                    </td>
                  </tr>
                );
              })}

              {(() => {
                const allSub  = colKeys.reduce((s, bk) => s + (data.totals[bk]?.submitted || 0), 0);
                const allDel  = colKeys.reduce((s, bk) => s + (data.totals[bk]?.deleted   || 0), 0);
                const allLive = colKeys.reduce((s, bk) => s + (data.totals[bk]?.live || 0), 0);
                const allRate = allSub > 0 ? Math.round(allDel / allSub * 1000) / 10 : 0;
                const overallCell = {
                  submitted: allSub,
                  deleted: allDel,
                  deletion_rate: allRate,
                  ...(typeof data.totals[colKeys[0]]?.live === 'number' ? { live: allLive } : {}),
                };
                return (
                  <tr style={{ borderTop: '2px solid var(--rh-border)', background: 'rgba(0,0,0,0.018)' }}>
                    <td style={{ padding: '10px 16px', whiteSpace: 'nowrap', width: 180 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                          background: 'rgba(0,0,0,0.07)', color: 'var(--rh-text-muted)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 12, fontWeight: 700,
                        }}>Σ</div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--rh-text)' }}>All Redditors</div>
                          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{allSub} submitted</div>
                        </div>
                      </div>
                    </td>
                    {colKeys.map((bk) => (
                      <td key={bk} style={{ padding: '8px 5px', textAlign: 'center' }}>
                        <Cell cell={data.totals[bk]} isTotal />
                      </td>
                    ))}
                    <td style={{ padding: '8px 5px', textAlign: 'center', borderLeft: '2px solid var(--rh-border)' }}>
                      <Cell cell={overallCell} isTotal />
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ padding: '8px 20px 12px', margin: 0, fontSize: 11, color: '#94a3b8' }}>
        * fewer than {SMALL_SAMPLE} submissions — interpret with caution.&nbsp;
        {footnote}
      </p>
    </div>
  );
}
